import Joi from 'joi';
import { isNullOrWhitespace } from '../../../utils/stringExtensions';
import { MAX_CHARS } from '../../AppraiserForm/constants/AppraiserInfoConstants';
import { AFFILIATION_TYPE } from '../constants/AffiliationType';
import { OBSERVATION_TYPE } from '../constants/observationType';
import { CURRENT_APPRAISER_ROLE } from '@shared/constants/currentAppraiserRole';
import { VALIDATION_ERROR_CODES } from '../constants/validationErrorCodes';

const ROLE_NOT_ELIGIBLE_ERROR_TEXT =
  'Current MDDAP Role <%=currentRoleName%> is not eligible to be an Observer.';
const MISSING_FIELD_ERROR_TEXT = 'Please enter all required fields.';

const validateStringInput = value => {
  if (isNullOrWhitespace(value)) {
    throw Error('value must not be an empty string.');
  }

  return value;
};

const validateWillingnessToTravel = value => {
  const willingnessToTravel = [
    value.willingToTravelToAfrica,
    value.willingToTravelToAsia,
    value.willingToTravelToAustralia,
    value.willingToTravelToEurope,
    value.willingToTravelToNorthAmerica,
    value.willingToTravelToSouthAmerica,
    value.willingToTravelToVirtual
  ];

  if (!willingnessToTravel.some(item => item)) {
    throw Error('At least one continent should be selected.');
  }

  return value;
};

const BASE_APPRAISER_SCHEMA = Joi.object({
  desiredRole: Joi.number().required(),
  currentRole: Joi.number().required(),
  affiliationType: Joi.number().required(),
  atlAffiliation: Joi.any().when('affiliationType', {
    is: Joi.number().valid(AFFILIATION_TYPE.ATL_AFFILIATION),
    then: Joi.date().required(),
    otherwise: Joi.any().equal(null)
  }),
  atmAffiliation: Joi.any().when('affiliationType', {
    is: Joi.valid(AFFILIATION_TYPE.ATM_AFFILIATION, AFFILIATION_TYPE.ATL_AFFILIATION),
    then: Joi.date().required(),
    otherwise: Joi.any().equal(null)
  }),
  timeZoneId: Joi.number().required(),
  city: Joi.string().max(MAX_CHARS).required().custom(validateStringInput),
  state: Joi.string().max(MAX_CHARS).required().custom(validateStringInput),
  country: Joi.string().max(MAX_CHARS).required().custom(validateStringInput),
  willingToTravelToAfrica: Joi.boolean().required(),
  willingToTravelToAsia: Joi.boolean().required(),
  willingToTravelToAustralia: Joi.boolean().required(),
  willingToTravelToEurope: Joi.boolean().required(),
  willingToTravelToNorthAmerica: Joi.boolean().required(),
  willingToTravelToSouthAmerica: Joi.boolean().required(),
  willingToTravelToVirtual: Joi.boolean().required(),
  assignedAppraisals: Joi.array()
    .items(
      Joi.object({
        id: Joi.number().min(0),
        name: Joi.string(),
        orgUnitNames: Joi.string()
      })
    )
    .allow(null)
})
  .custom(validateWillingnessToTravel)
  .unknown(true);

const APPRAISER_FORM_SCHEMA = BASE_APPRAISER_SCHEMA.keys({
  domainExperiences: Joi.array()
    .items(
      Joi.object({
        domain: Joi.number().min(-1).required(),
        yearsOfExperience: Joi.number().min(0).required(),
        role: Joi.number().min(0).required(),
        hasDomainAccess: Joi.boolean().optional().allow(null)
      })
    )
    .required(),
  observation: Joi.number().when('currentRole', {
    is: Joi.number().valid(
      CURRENT_APPRAISER_ROLE.ATM_CANDIDATE,
      CURRENT_APPRAISER_ROLE.ATM_APPRENTICE,
      CURRENT_APPRAISER_ROLE.ATL_CANDIDATE,
      CURRENT_APPRAISER_ROLE.ATL_APPRENTICE
    ),
    then: Joi.number()
      .required()
      .custom((value, helpers) => {
        if (value !== OBSERVATION_TYPE.NA) {
          return helpers.error(VALIDATION_ERROR_CODES.OBSERVER_ROLE_NOT_ELIGIBLE);
        }
        return value;
      })
      .message(ROLE_NOT_ELIGIBLE_ERROR_TEXT),
    otherwise: Joi.number()
      .valid(...Object.values(OBSERVATION_TYPE))
      .required()
  })
}).messages({
  '*': MISSING_FIELD_ERROR_TEXT
});

const AppraiserInputValidator = {
  validateAssignLicenseToAppraiserData: input => {
    return BASE_APPRAISER_SCHEMA.validate(input, { abortEarly: true });
  },

  validateEditAppraiserLicenseData: input => {
    return APPRAISER_FORM_SCHEMA.validate(input, { abortEarly: true });
  }
};

export default AppraiserInputValidator;
